import type * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  DataTable,
  StatusChip,
  type DataTableState,
  type IDataColumn,
  type IDataSelection
} from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IInternalSchemaField } from '../../api/internal-field-contracts';
import type { IMyTaskFlags, IWorkItemRow, WorkItemState } from '../../api/work-item-contracts';
import { formatDay } from '../../components/days';
import { CardLink, personHubPath, type ICardLinkState } from '../../components/participant-link';
import {
  decisionLabel,
  isAboutMember,
  memberParts,
  stateChip,
  triggerLabel,
  workItemName
} from '../../work-items';
import { removeWrites, StaffValue } from './staff-columns';
import { NewMark, TodayChip, markRowProps, todayShown } from './task-marks';

/**
 * A work item's row, as a table whose rows open the item beside the list (SR-222, F9's S11) —
 * the one rendering of it, for the compliance queues and for My tasks, which draws the same rows
 * under a fixed query. The `ResponsesTable` precedent: one row type, one table, so a column means
 * the same wherever it is read.
 *
 * **Everything in a row is as survey-api sent it.** The due day is the server's (`dueOn`, null
 * once reviewed); the pending and final counts are its count of the CRD's live disclosures; the
 * state is the stored one. One thing is read here rather than sent: **overdue**, a due day before
 * today's UTC day — the demo's reading of CMP-09, a render decision over a sent date, which
 * decides only that the day is drawn in the `danger` tone (F9's R5). No due day is computed.
 *
 * **A row opens its item three ways**, as a response's row does: a click on it, Enter on it, and
 * the subject as a link, for a new tab. Each leaves the list's search in the history entry, so the
 * item opens beside the list it came from (`SplitView`). The participant is a link of its own, to
 * their hub, full width: it is another list's record.
 *
 * It renders what it is handed: the filters, the pager, what an empty page says and what is ticked
 * for a bulk decision (`selected`, each box named by `workItemName`) are the caller's. The
 * hub draws it too, for a member's open items: with no list `search`, an item opens full width,
 * and with no participant column, since the member heads its part (`work-items.tsx`).
 *
 * **A survey queue's staff fields are columns of the picker's** (`staffFields`, F9's Task 8),
 * after every other column, each drawing the row's `internal` value by the field's type
 * (`StaffValue`) — Quality rating and Travel shown until a viewer hides them, the rest hidden until
 * asked for. Where no staff field can be offered (All, My tasks), `pickerNote` says why.
 *
 * `marks`, My tasks' alone, hands a row's flags in: the row is then marked as My tasks marks its
 * own (`task-marks.tsx`).
 */
export function WorkItemsTable({
  rows,
  state,
  search,
  itemPath,
  queueColumn,
  participantColumn = true,
  pickerId,
  selected,
  onSelect,
  staffFields = [],
  pickerNote,
  columns,
  marks,
  stickyTop
}: {
  rows: IWorkItemRow[];
  /** What the caller says instead of rows — a read out, failed, or a page with nothing on it. */
  state?: DataTableState;
  /** The list's own query, `?` included, kept in each item link's history state — absent where
   *  the table is not the list (the hub), and an item then opens full width. */
  search?: string;
  /** The item's address on this list — the queue's or My tasks'. */
  itemPath: (workItemId: string) => string;
  /** Whether the row says its queue — on All and on My tasks, where rows of several sit together. */
  queueColumn: boolean;
  /** Whether the row names whose item it is — every caller but the hub, whose member heads it. */
  participantColumn?: boolean;
  /** Where a viewer's column choice is kept — none on the hub, which offers no picker. */
  pickerId?: string;
  /** The ids ticked for a bulk decision, where the list offers one. */
  selected?: ReadonlySet<string>;
  onSelect?: (ids: string[]) => void;
  /** The staff fields the rows were asked with (`fields=`), offered as columns. */
  staffFields?: readonly IInternalSchemaField[];
  /** Said in the column picker — why no staff field is offered. */
  pickerNote?: React.ReactNode;
  /** The columns as the page arranged them (`workItemColumns`, the compliance queues' Columns
   *  sheet) — absent, the table's own, under the design table's picker. */
  columns?: IDataColumn<IWorkItemRow>[];
  /** A row's My tasks flags, marked on it — My tasks' alone. */
  marks?: (row: IWorkItemRow) => Nullable<IMyTaskFlags>;
  /** The page scrolls, the header sticking this far under the window's top (`DataTable`'s). */
  stickyTop?: string;
}) {
  const navigate = useNavigate();
  const linkState = linkStateOf(search);
  const selectable: IDataSelection<IWorkItemRow> | undefined =
    selected && onSelect ? { selected, onSelect, name: workItemName } : undefined;

  return (
    <DataTable
      columns={
        columns ?? workItemColumns({ search, itemPath, queueColumn, participantColumn, staffFields, marks })
      }
      rows={rows.length === 0 ? null : rows}
      rowKey={(row) => row.id}
      rowProps={(row) =>
        ({
          'data-work-item': row.id,
          ...markRowProps(marks?.(row))
        }) as React.HTMLAttributes<HTMLTableRowElement>
      }
      onOpen={(row) => navigate(itemPath(row.id), { state: linkState })}
      state={state}
      pickerId={pickerId}
      pickerNote={pickerNote}
      selectable={selectable}
      stickyTop={stickyTop}
    />
  );
}

function linkStateOf(search: string | undefined): ICardLinkState | undefined {
  return search === undefined ? undefined : { search, list: true };
}

/** A work item row's columns, as `WorkItemsTable` draws them — a staff field's after the rest,
 *  Quality rating and Travel shown by default. */
export function workItemColumns({
  search,
  itemPath,
  queueColumn,
  participantColumn = true,
  staffFields = [],
  marks
}: {
  search?: string;
  itemPath: (workItemId: string) => string;
  queueColumn: boolean;
  participantColumn?: boolean;
  staffFields?: readonly IInternalSchemaField[];
  marks?: (row: IWorkItemRow) => Nullable<IMyTaskFlags>;
}): IDataColumn<IWorkItemRow>[] {
  const linkState = linkStateOf(search);
  const today = new Date().toISOString().slice(0, 10);
  const shownByDefault = new Set(removeWrites(staffFields).map((field) => field.code));

  return [
    {
      id: 'subject',
      header: 'Subject',
      cell: (row) => (
        <>
          <Link
            to={itemPath(row.id)}
            state={linkState}
            data-work-item-link=""
            className="font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            {row.subject}
          </Link>
          {marks?.(row)?.isNew && <NewMark />}
        </>
      )
    },
    ...(participantColumn
      ? [
          {
            id: 'participant',
            header: 'Advisor or team',
            cell: (row: IWorkItemRow) => (
              <span className="grid">
                <CardLink to={personHubPath(row.participant.id)}>
                  {row.participant.displayName}
                </CardLink>
                {isAboutMember(row) && (
                  <span data-member className="text-xs text-muted-foreground">
                    About {memberParts(row).join(' · ')}
                  </span>
                )}
                {row.participant.firmName && (
                  <span className="text-xs text-muted-foreground">
                    {row.participant.firmName}
                  </span>
                )}
              </span>
            )
          }
        ]
      : []),
    ...(queueColumn
      ? [
          {
            id: 'queue',
            header: 'Queue',
            className: 'text-xs',
            cell: (row: IWorkItemRow) => row.surveyCode
          }
        ]
      : []),
    {
      id: 'trigger',
      header: 'Raised by',
      cell: (row) => <RaisedBy row={row} />
    },
    {
      id: 'raised',
      header: 'Raised',
      optional: true,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (row) => formatDateTime(row.raisedAt)
    },
    {
      id: 'due',
      header: 'Due',
      cell: (row) => <Due dueOn={row.dueOn} today={today} dueToday={marks?.(row)?.dueToday} />
    },
    {
      id: 'disclosures',
      header: 'Pending / final',
      optional: true,
      numeric: true,
      cell: (row) =>
        row.disclosures ? (
          <span className="tabular-nums">
            {row.disclosures.pending} / {row.disclosures.final}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
    },
    {
      id: 'assignee',
      header: 'Assigned to',
      className: 'text-xs',
      cell: (row) =>
        row.assignee ? (
          <span className="wrap-anywhere">{row.assignee.email}</span>
        ) : (
          <span className="text-muted-foreground">Unassigned</span>
        )
    },
    {
      id: 'state',
      header: 'State',
      cell: (row) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <WorkItemStateChip state={row.state} />
          {row.state === 'reviewed' && row.decision && (
            <span className="text-xs text-muted-foreground">{decisionLabel(row.decision)}</span>
          )}
        </span>
      )
    },
    {
      id: 'updated',
      header: 'Last modified',
      optional: true,
      className: 'text-xs text-muted-foreground',
      cell: (row) => (
        <span className="grid">
          <span className="whitespace-nowrap">{formatDateTime(row.updatedAt)}</span>
          <span className="wrap-anywhere">{row.updatedBy?.email ?? 'System'}</span>
        </span>
      )
    },
    ...staffFields.map(
      (field): IDataColumn<IWorkItemRow> => ({
        id: `field:${field.code}`,
        header: field.name,
        optional: true,
        hidden: !shownByDefault.has(field.code),
        className: 'text-xs',
        cell: (row) => <StaffValue text={row.internal?.[field.code]} field={field} />
      })
    )
  ];
}

/** An item's state, as its chip draws it — `data-state` the stored spelling, which is what a
 *  spec reads; the words are `work-items.ts`'. */
export function WorkItemStateChip({ state }: { state: WorkItemState }) {
  const { label, tone } = stateChip(state);
  return (
    <span data-state={state} className="inline-flex">
      <StatusChip tone={tone} dot>
        {label}
      </StatusChip>
    </span>
  );
}

/** What raised the item: the trigger in words, and the criteria that met it by name and version
 *  (CMP-08) — visible, and the chip's description for a reader moving by controls. */
function RaisedBy({ row }: { row: IWorkItemRow }) {
  const criteria = row.criteria.map((criterion) => `${criterion.name} · v${criterion.version}`);
  const describedBy = criteria.length > 0 ? `work-item-criteria-${row.id}` : undefined;
  return (
    <span className="grid justify-items-start gap-0.5">
      <span aria-describedby={describedBy}>
        <StatusChip>{triggerLabel(row.trigger)}</StatusChip>
      </span>
      {describedBy && (
        <span id={describedBy} className="text-xs text-muted-foreground">
          {criteria.join('; ')}
        </span>
      )}
    </span>
  );
}

/** The due day as sent, and "Overdue" beside one before today's UTC day (F9's R5). `yyyy-MM-dd`
 *  compares as text. The compliance table's and My tasks' — whose flag says "Today" too. */
export function Due({
  dueOn,
  today,
  dueToday = false
}: {
  dueOn: Nullable<string>;
  today: string;
  dueToday?: boolean;
}) {
  if (dueOn === null) return <span className="text-muted-foreground">—</span>;
  const overdue = dueOn < today;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 whitespace-nowrap">
      <span className={overdue ? 'font-medium text-[var(--color-deep-magenta)]' : undefined}>
        {formatDay(dueOn)}
      </span>
      {overdue && <StatusChip tone="danger">Overdue</StatusChip>}
      {todayShown(overdue, dueToday) && <TodayChip />}
    </span>
  );
}
